<template>
  <div
    class="summary variable"
    :class="variableAttribute.mutability === Mutability.READ_ONLY && 'italic text-neutral-500 text-opacity-60'"
  >
    <span class="flex items-center gap-x-2 max-md:flex-wrap max-md:gap-y-1">
      <span class="data-type flex pl-1 pr-1">
        <InfuseTooltip v-if="type === 'variable'" :content="t(`cpms.configuration.dataType.${variable.dataType}`)">
          <font-awesome-icon
            :icon="['fal', getDataTypeIcon(variable.dataType)]"
            :class="getDataTypeClass(variable.dataType)"
          />
        </InfuseTooltip>
        <InfuseTooltip v-else :content="t(`cpms.configuration.attributeType.${variableAttribute.type}`)">
          <font-awesome-icon
            :icon="['fal', getAttributeTypeIcon(variableAttribute.type)]"
            :class="getAttributeTypeClass(variableAttribute.type)"
          />
        </InfuseTooltip>
      </span>

      <span class="variable-name truncate text-sm max-md:min-w-0 max-md:flex-1 md:min-w-[25%] md:max-w-[25%]">
        {{ variable.name }}<span v-if="variable.instance">: {{ variable.instance }}</span>
      </span>

      <span class="variable-value truncate text-xs max-md:order-last max-md:basis-full max-md:pl-7 md:min-w-[50%] md:max-w-[50%]">
        <span v-if="!isLoading"> {{ variableAttribute.value }}</span>
        <span v-else class="variable-unit skeleton inline-block w-16"> &emsp; </span>

        <span v-if="variable.unit && !isLoading" class="variable-unit italic">
          {{ variable.unit }}
        </span>
      </span>

      <span class="flex justify-end max-md:ml-auto md:w-full">
        <span class="px-1.5">
          <InfuseTooltip :content="t('cpms.configuration.action.setVariable.tooltip')">
            <font-awesome-icon
              v-if="variableAttribute.mutability !== Mutability.READ_ONLY"
              v-permission="setVariablePermission"
              :icon="['far', 'pen-to-square']"
              class="cursor-pointer text-neutral-400 hover:text-neutral-500"
              size="sm"
              @click="onSetVariable(variable, variableAttribute.type)"
            />
          </InfuseTooltip>
        </span>
        <span class="px-1.5" v-if="!onboarding">
          <InfuseTooltip :content="t('cpms.configuration.action.getVariable.tooltip')">
            <font-awesome-icon
              v-permission="{ id: commandPermissions.GET_CONFIGURATION, hide: false }"
              :icon="['far', 'arrow-down']"
              class="cursor-pointer text-neutral-400 hover:text-neutral-500"
              size="sm"
              @click="onGetVariable(variable, variableAttribute.type)"
            />
          </InfuseTooltip>
        </span>
        <span class="px-1.5" v-if="onboarding">
          <InfuseTooltip :content="t('cpms.configuration.action.removeVariable.tooltip')">
            <font-awesome-icon
              v-permission="{ id: onboardingPermissions.REMOVE_CONFIGURATION, hide: false }"
              :icon="['far', 'trash-can']"
              class="cursor-pointer text-neutral-400 hover:text-neutral-500"
              size="sm"
              @click="onRemoveVariable(variable, variableAttribute.type)"
            />
          </InfuseTooltip>
        </span>
      </span>
    </span>
  </div>
</template>

<script setup lang="ts">
import {
  AttributeType,
  DataType,
  Mutability,
  useComponentModelStore,
  Variable,
  VariableAttribute
} from '@infuse/cpms/charging-station/store';
import { InfuseTooltip } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';
import { vPermission } from '@infuse/iam/permission/directives';
import { commandPermissions } from '@infuse/api/cpms/operator';
import { computed } from 'vue';
import { onboardingPermissions } from '@infuse/api/cpms/onboarding';

interface Props {
  type: 'variable' | 'variableAttribute';
  variable: Variable;
  variableAttribute: VariableAttribute;
  loading?: boolean;
  onboarding?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  onboarding: false
});

const store = useComponentModelStore();

const isLoading = computed(() => props.loading || store.isLoadingVariable(props.variable));

const { t } = useI18n();

interface Emits {
  (e: 'setVariable', variable: Variable, attributeType: AttributeType): void;

  (e: 'getVariable', variable: Variable, attributeType: AttributeType): void;

  (e: 'removeVariable', variable: Variable, attributeType: AttributeType): void;
}

const emit = defineEmits<Emits>();

function onSetVariable(variable: Variable, attributeType: AttributeType) {
  if (isLoading.value) {
    return;
  }

  emit('setVariable', variable, attributeType);
}

function onGetVariable(variable: Variable, attributeType: AttributeType) {
  if (isLoading.value) {
    return;
  }

  emit('getVariable', variable, attributeType);
}

function onRemoveVariable(variable: Variable, attributeType: AttributeType) {
  if (isLoading.value) {
    return;
  }

  emit('removeVariable', variable, attributeType);
}

const setVariablePermission = computed(() => {
  if (!props.onboarding) {
    return { id: commandPermissions.SET_CONFIGURATION, hide: false };
  }

  return { id: onboardingPermissions.UPDATE_CONFIGURATION, hide: false };
});

function getDataTypeClass(dataType: DataType): string {
  switch (dataType) {
    case DataType.BOOLEAN:
      return 'text-[#8865ce]';
    case DataType.DATE_TIME:
      return 'text-neutral-500';
    case DataType.DECIMAL:
    case DataType.INTEGER:
      return 'text-[#6897BB]';
    case DataType.STRING:
      return 'text-[#6A8759]';
    case DataType.MEMBER_LIST:
    case DataType.OPTION_LIST:
    case DataType.SEQUENCE_LIST:
      return 'text-[#BBB529]';
  }
}

function getDataTypeIcon(dataType: DataType): string {
  switch (dataType) {
    case DataType.BOOLEAN:
      return 'circle-b';
    case DataType.DATE_TIME:
      return 'circle-d';
    case DataType.DECIMAL:
      return 'circle-n';
    case DataType.INTEGER:
      return 'circle-i';
    case DataType.STRING:
      return 'circle-s';
    case DataType.MEMBER_LIST:
    case DataType.OPTION_LIST:
    case DataType.SEQUENCE_LIST:
      return 'circle-l';
  }
}

function getAttributeTypeClass(attributeType: AttributeType): string {
  switch (attributeType) {
    case AttributeType.TARGET:
    case AttributeType.ACTUAL:
      return 'text-neutral-500';
    case AttributeType.MIN_SET:
      return 'text-[#6897BB]';
    case AttributeType.MAX_SET:
      return 'text-[#CC7832]';
  }
}

function getAttributeTypeIcon(attributeType: AttributeType): string {
  switch (attributeType) {
    case AttributeType.TARGET:
      return 'circle-t';
    case AttributeType.ACTUAL:
      return 'circle-a';
    case AttributeType.MIN_SET:
    case AttributeType.MAX_SET:
      return 'circle-m';
  }
}
</script>

<style scoped>
.variable {
  cursor: default !important;
}
</style>
